import React, { useEffect } from "react";
import {
    DonutLarge as WordWheelIcon,
    Extension as JigsawIcon,
    GridOn as GridIcon,
    Message as MessageIcon,
    PhoneIphone as PhoneIcon,
    Search as SearchIcon,
    ViewModule as SlidingIcon,
} from "@mui/icons-material";
import {
    Box,
    Card,
    Divider,
    Grid,
    Typography
} from "@mui/material";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { TEXT } from "../../dark-theme/palette";
import {
    APP_SITE_ID,
    resolveAppSiteEntry,
} from "../../config/appSiteLinks";
import {
    NATIVE_IOS_ANDROID_AVAILABLE,
    JIGSAW_MUSE_GOOGLE_PLAY_URL,
    SHOW_JIGSAW_GOOGLE_PLAY_BADGE,
    SHOW_SLIDING_GOOGLE_PLAY_BADGE,
    SHOW_WORD_WHEEL_APP_STORE_BADGE,
    SHOW_WORD_WHEEL_GOOGLE_PLAY_BADGE,
    WORD_WHEEL_QUEST_APP_STORE_URL,
    WORD_WHEEL_QUEST_GOOGLE_PLAY_URL,
} from "../../config/nativeAppsVisibility";
import { usePagesTranslation, normalizeLangParam } from "../locales";
import { trackEvent } from "../../services/GoogleAnalytics";
import AppStoreBadgeLink from "../components/AppStoreBadgeLink";
import GooglePlayBadgeLink from "../components/GooglePlayBadgeLink";
import FooterContactUsCard from "./FooterContactUsCard";
import slidingPuzzleQuestIcon from "../../assets/app-icons/sliding-puzzle-quest-app-icon.png";
import wordWheelQuestIcon from "../../assets/app-icons/word-wheel-quest-app-icon.png";

const JIGSAW_MUSE_APP_ICON = `${process.env.PUBLIC_URL || ""}/ad/jigsaw-muse-app-icon-512.png`;

const PUZZLE_TYPE_CARDS = [
    { key: "jigsaw", Icon: JigsawIcon, titleKey: "landing.jigsaw-title", descKey: "landing.jigsaw-desc" },
    { key: "word-wheel", Icon: WordWheelIcon, titleKey: "landing.word-wheel-title", descKey: "landing.word-wheel-desc" },
    { key: "sliding", Icon: SlidingIcon, titleKey: "landing.sliding-title", descKey: "landing.sliding-desc" },
    { key: "crossword", Icon: GridIcon, titleKey: "landing.crossword-title", descKey: "landing.crossword-desc" },
    { key: "hidden-msg", Icon: MessageIcon, titleKey: "landing.hidden-msg-title", descKey: "landing.hidden-msg-desc" },
    { key: "word-search", Icon: SearchIcon, titleKey: "landing.word-search-title", descKey: "landing.word-search-desc" },
];

const WORD_WHEEL_FEATURE_KEYS = [
    { titleKey: "marketing.wordWheelQuest.feature1Title", descKey: "marketing.wordWheelQuest.feature1Desc" },
    { titleKey: "marketing.wordWheelQuest.feature2Title", descKey: "marketing.wordWheelQuest.feature2Desc" },
    { titleKey: "marketing.wordWheelQuest.feature3Title", descKey: "marketing.wordWheelQuest.feature3Desc" },
    { titleKey: "marketing.wordWheelQuest.feature4Title", descKey: "marketing.wordWheelQuest.feature4Desc" },
];

const MarketingMain = () => {
    const { appId } = useParams();
    const appEntry = resolveAppSiteEntry(appId);
    const [searchParams] = useSearchParams();
    const urlLang = normalizeLangParam(searchParams.get("lang"));
    const { t } = usePagesTranslation(urlLang);
    useEffect(() => {
        trackEvent('entered', { name: 'MarketingMain' });
    }, []);

    useEffect(() => {
        if (!appEntry) return undefined;
        const title =
            appEntry.id === APP_SITE_ID.WEB ? t("marketing.title") : appEntry.displayName;
        document.title =
            appEntry.id === APP_SITE_ID.WEB
                ? title
                : `${title} — ${appEntry.displayName} | Puzzle Interact`;
        return () => {
            document.title = process.env.REACT_APP_SITE_NAME || "Puzzle Interact";
        };
    }, [appEntry, t]);

    if (!appEntry) {
        return <Navigate to={`/marketing/${APP_SITE_ID.WEB}`} replace />;
    }

    const isWordWheelQuest = appEntry.id === APP_SITE_ID.WORD_WHEEL_QUEST;
    const pageTitle =
        appEntry.id === APP_SITE_ID.WEB ? t("marketing.title") : appEntry.displayName;
    const pageIntro =
        appEntry.id === APP_SITE_ID.WEB
            ? null
            : t("marketing.appIntro", { appName: appEntry.displayName });
    const pageSubtitle = isWordWheelQuest
        ? t("marketing.wordWheelQuest.subtitle")
        : t(
              NATIVE_IOS_ANDROID_AVAILABLE
                  ? "marketing.subtitle"
                  : "marketing.subtitle-web-only",
          );

    return (
        <Box sx={{
            width: "100%",
            minHeight: "100vh",
            py: 4
        }}>
            <Box sx={{
                maxWidth: "1080px",
                mx: "auto",
                px: 3
            }}>
                <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                        textAlign: "center",
                        mb: pageIntro ? 1.5 : 2,
                        fontWeight: 700,
                        color: "#3BA666"
                    }}
                >
                    {pageTitle}
                </Typography>

                {pageIntro && (
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: "center",
                            mb: 1.5,
                            color: TEXT.secondary,
                            maxWidth: 640,
                            mx: "auto",
                        }}
                    >
                        {pageIntro}
                    </Typography>
                )}

                <Typography variant="body1" sx={{ textAlign: "center", mb: 4, color: "text.secondary" }}>
                    {pageSubtitle}
                </Typography>

                {isWordWheelQuest ? (
                    <>
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: TEXT.primary }}>
                            {t("marketing.wordWheelQuest.aboutTitle")}
                        </Typography>
                        <Card sx={{ p: 3, mb: 4, boxShadow: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                                <Box
                                    component="img"
                                    src={wordWheelQuestIcon}
                                    alt={appEntry.displayName}
                                    title={appEntry.displayName}
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: "12px",
                                        objectFit: "cover",
                                        flexShrink: 0,
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.16)",
                                    }}
                                />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {appEntry.displayName}
                                </Typography>
                            </Box>
                            <Typography variant="body1" color="text.secondary">
                                {t("marketing.wordWheelQuest.aboutBody")}
                            </Typography>
                        </Card>

                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: TEXT.primary }}>
                            {t("marketing.features-title")}
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            {WORD_WHEEL_FEATURE_KEYS.map(({ titleKey, descKey }) => (
                                <Grid key={titleKey} size={{ xs: 12, sm: 6 }}>
                                    <Card sx={{ height: "100%", p: 2, boxShadow: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {t(titleKey)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t(descKey)}
                                        </Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        <Divider sx={{ my: 4 }} />

                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: TEXT.primary }}>
                            {t("marketing.types-apps-title")}
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            {SHOW_WORD_WHEEL_GOOGLE_PLAY_BADGE || SHOW_WORD_WHEEL_APP_STORE_BADGE ? (
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Card
                                        sx={{
                                            height: "100%",
                                            p: 3,
                                            boxShadow: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            textAlign: "center",
                                            gap: 1.5,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <Box
                                                component="img"
                                                src={wordWheelQuestIcon}
                                                alt={appEntry.displayName}
                                                title={appEntry.displayName}
                                                sx={{
                                                    width: 64,
                                                    height: 64,
                                                    borderRadius: "14px",
                                                    objectFit: "cover",
                                                    flexShrink: 0,
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                                                }}
                                            />
                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                {appEntry.displayName}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {t("marketing.wordWheelQuest.aboutBody")}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 1.5,
                                                justifyContent: "center",
                                                mt: "auto",
                                            }}
                                        >
                                            {SHOW_WORD_WHEEL_APP_STORE_BADGE ? (
                                                <AppStoreBadgeLink
                                                    height={48}
                                                    href={WORD_WHEEL_QUEST_APP_STORE_URL}
                                                    ariaLabel={t(
                                                        "landing.word-wheel-app-store-badge-aria",
                                                        "Get Word Wheel Quest on the App Store (opens in a new tab)",
                                                    )}
                                                />
                                            ) : null}
                                            {SHOW_WORD_WHEEL_GOOGLE_PLAY_BADGE ? (
                                                <GooglePlayBadgeLink
                                                    height={48}
                                                    href={WORD_WHEEL_QUEST_GOOGLE_PLAY_URL}
                                                    ariaLabel={t(
                                                        "landing.word-wheel-google-play-badge-aria",
                                                        "Get Word Wheel Quest on Google Play (opens in a new tab)",
                                                    )}
                                                />
                                            ) : null}
                                        </Box>
                                    </Card>
                                </Grid>
                            ) : (
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Card sx={{ height: "100%", p: 3, boxShadow: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                                            <PhoneIcon sx={{ color: "#3BA666", fontSize: 32 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                {t("marketing.app-iphone")}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {t("marketing.wordWheelQuest.iphoneDesc")}
                                        </Typography>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>
                    </>
                ) : (
                    <>
                {/* Types of Puzzles */}
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: TEXT.primary }}>
                    {t("marketing.types-puzzles-title")}
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        mb: 4,
                        columnGap: 2,
                        rowGap: 4,
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                    }}
                >
                    {PUZZLE_TYPE_CARDS.map(({ key, Icon, titleKey, descKey }) => (
                        <Card
                            key={key}
                            sx={{ textAlign: "center", p: 3, boxShadow: 1 }}
                        >
                            <Icon sx={{ fontSize: 48, color: "#3BA666", mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {t(titleKey)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t(descKey)}
                            </Typography>
                        </Card>
                    ))}
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* Features */}
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: TEXT.primary }}>
                    {t("marketing.features-title")}
                </Typography>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ height: "100%", p: 2, boxShadow: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t("landing.benefit-brain-title")}</Typography>
                            <Typography variant="body2" color="text.secondary">{t("landing.benefit-brain-desc")}</Typography>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ height: "100%", p: 2, boxShadow: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t("landing.benefit-vocab-title")}</Typography>
                            <Typography variant="body2" color="text.secondary">{t("landing.benefit-vocab-desc")}</Typography>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ height: "100%", p: 2, boxShadow: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t("landing.feature-autosave-title")}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>{t("landing.feature-autosave-desc")}</Typography>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ height: "100%", p: 2, boxShadow: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t("landing.feature-achievements-title")}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>{t("landing.feature-achievements-desc")}</Typography>
                        </Card>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                {/* Applications */}
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: TEXT.primary }}>
                    {t("marketing.types-apps-title")}
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
                        gap: 2,
                        mb: 4,
                        alignItems: "stretch",
                    }}
                >
                    {SHOW_JIGSAW_GOOGLE_PLAY_BADGE ? (
                        <Card
                            sx={{
                                p: 3,
                                boxShadow: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                textAlign: "center",
                                gap: 1.5,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box
                                    component="img"
                                    src={JIGSAW_MUSE_APP_ICON}
                                    alt="Jigsaw Muse"
                                    title="Jigsaw Muse"
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: "14px",
                                        objectFit: "cover",
                                        flexShrink: 0,
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                                    }}
                                />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Jigsaw Muse
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {t("marketing.jigsaw-muse-promo")}
                            </Typography>
                            <GooglePlayBadgeLink
                                height={48}
                                sx={{ mt: "auto" }}
                                href={JIGSAW_MUSE_GOOGLE_PLAY_URL}
                                ariaLabel={t(
                                    "landing.jigsaw-google-play-badge-aria",
                                    "Get Jigsaw Muse on Google Play (opens in a new tab)",
                                )}
                            />
                        </Card>
                    ) : null}
                    {SHOW_WORD_WHEEL_GOOGLE_PLAY_BADGE || SHOW_WORD_WHEEL_APP_STORE_BADGE ? (
                        <Card
                            sx={{
                                p: 3,
                                boxShadow: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                textAlign: "center",
                                gap: 1.5,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box
                                    component="img"
                                    src={wordWheelQuestIcon}
                                    alt="Word Wheel Quest"
                                    title="Word Wheel Quest"
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: "14px",
                                        objectFit: "cover",
                                        flexShrink: 0,
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                                    }}
                                />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Word Wheel Quest
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {t("marketing.wordWheelQuest.subtitle")}
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flexWrap: "nowrap",
                                    gap: 1,
                                    alignItems: "center",
                                    mt: "auto",
                                }}
                            >
                                {SHOW_WORD_WHEEL_APP_STORE_BADGE ? (
                                    <AppStoreBadgeLink
                                        height={48}
                                        href={WORD_WHEEL_QUEST_APP_STORE_URL}
                                        ariaLabel={t(
                                            "landing.word-wheel-app-store-badge-aria",
                                            "Get Word Wheel Quest on the App Store (opens in a new tab)",
                                        )}
                                    />
                                ) : null}
                                {SHOW_WORD_WHEEL_GOOGLE_PLAY_BADGE ? (
                                    <GooglePlayBadgeLink
                                        height={48}
                                        href={WORD_WHEEL_QUEST_GOOGLE_PLAY_URL}
                                        ariaLabel={t(
                                            "landing.word-wheel-google-play-badge-aria",
                                            "Get Word Wheel Quest on Google Play (opens in a new tab)",
                                        )}
                                    />
                                ) : null}
                            </Box>
                        </Card>
                    ) : null}
                    {SHOW_SLIDING_GOOGLE_PLAY_BADGE ? (
                        <Card
                            sx={{
                                p: 3,
                                boxShadow: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                textAlign: "center",
                                gap: 1.5,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box
                                    component="img"
                                    src={slidingPuzzleQuestIcon}
                                    alt="Sliding Puzzle Quest"
                                    title="Sliding Puzzle Quest"
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: "14px",
                                        objectFit: "cover",
                                        flexShrink: 0,
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                                    }}
                                />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Sliding Puzzle Quest
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {t("marketing.sliding-quest-promo")}
                            </Typography>
                            <GooglePlayBadgeLink height={48} sx={{ mt: "auto" }} />
                        </Card>
                    ) : null}
                </Box>

                <Divider sx={{ my: 4 }} />
                    </>
                )}

                <FooterContactUsCard t={t} appId={appEntry?.id} />
            </Box>
        </Box>
    );
};

export default MarketingMain;
